import { useCallback, useRef, useState } from 'react';
import axios from 'axios';

import { PPA_CHART_PAGE_LIMIT } from 'constants/ppa';
import type {
    PpaBubbleLegendEntry,
    PpaChartBinConfig,
    PpaChartKpi,
} from 'components/pages/PackArchitecture/pricePackArchitecture.types';
import { ppaUrls } from 'config/urls';

export type KpiSelection = {
    id: string;
    is_bubble_size: boolean;
};

export type TimePeriodFilter = {
    start_timestamp: number;
    end_timestamp: number;
};

export type PpaFilters = {
    dim1_id_list?: number[];
    dim2_id_list?: number[];
};

export type AggregateConfig = {
    dimension: string | null;
    attribute_type: string;
    is_attribute: boolean | null;
    is_bubble: boolean;
};

export type UserInput = {
    id: string;
    input_val: string;
};

export type PricePackArchitectureRequest = {
    kpi_ids: KpiSelection[];
    time_period: TimePeriodFilter;
    filters?: PpaFilters;
    aggregates: AggregateConfig[];
    user_inputs?: UserInput[];
    limit?: number;
    offset?: number;
};

export type KpiItem = {
    kpi_id: string;
    kpi_name: string;
    display_label: string;
    valuePrefix?: string;
    valueSuffix?: string;
    value: string;
    raw_value?: number | null;
    growth_percentage?: number;
    trend_direction?: 'up' | 'down' | 'neutral' | string;
    comparison_period?: string;
};

export type SegmentItem = {
    dimension: string;
    attribute_type: string;
    ex_id: string;
    description: string;
    kpis: KpiItem[];
};

export type AxisGroup = {
    id_type: string;
    id: number;
    data: SegmentItem[];
};

export type PricePackGroupMember = {
    ex_id?: string;
    description?: string;
};

export type PricePackGroup = {
    dimension: string;
    attribute_type: string;
    ex_id: string;
    description: string;
    data: AxisGroup[];
    is_own?: boolean;
    /** Present only on the consolidated `__other__` ("Other Vendor") node. */
    members?: PricePackGroupMember[];
};

export type PricePackArchitectureResponse = {
    data: PricePackGroup[];
    total_count: number;
    limit: number;
    offset: number;
    bin_config?: PpaChartBinConfig;
    kpis?: PpaChartKpi[];
    /** PA-03: bubble-represents legend, sorted by the bubble-size KPI, top 80
     * individually coloured plus a trailing "Other {Dimension}" entry. */
    bubble_legend?: PpaBubbleLegendEntry[];
};

export type PricePackBubbleRow = PricePackGroup;

const DEFAULT_LIMIT = PPA_CHART_PAGE_LIMIT;
const DEFAULT_OFFSET = 0;

const normalizeFilters = (filters?: PpaFilters): PpaFilters => ({
    dim1_id_list: Array.isArray(filters?.dim1_id_list) ? filters?.dim1_id_list : [],
    dim2_id_list: Array.isArray(filters?.dim2_id_list) ? filters?.dim2_id_list : [],
});

const normalizeRequest = (payload: PricePackArchitectureRequest): PricePackArchitectureRequest => ({
    ...payload,
    filters: normalizeFilters(payload.filters),
    limit: typeof payload.limit === 'number' ? payload.limit : DEFAULT_LIMIT,
    offset: typeof payload.offset === 'number' ? payload.offset : DEFAULT_OFFSET,
});

export function useFetchPricePackArchitecture() {
    const [data, setData] = useState<PricePackArchitectureResponse | null>(null);
    const [rows, setRows] = useState<PricePackBubbleRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [isPaginatedLoading, setIsPaginatedLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const abortRef = useRef<AbortController | null>(null);

    const fetchPricePackArchitecture = useCallback(async (payload: PricePackArchitectureRequest) => {
        const normalizedPayload = normalizeRequest(payload);

        // Cancel previous in-flight call to avoid stale overwrite during rapid updates.
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const isPaginatedFetch = (normalizedPayload.offset ?? DEFAULT_OFFSET) > DEFAULT_OFFSET;
        setIsPaginatedLoading(isPaginatedFetch);

        setLoading(true);
        setError(null);

        if (!isPaginatedFetch) {
            setRows([]);
        }

        try {
            const response = await axios.post<PricePackArchitectureResponse>(
                ppaUrls.getPricePackArchitectureUrl,
                normalizedPayload,
                { signal: controller.signal },
            );
            setData(response.data);
            setRows((previousRows) => {
                const incomingRows = response.data?.data ?? [];
                if ((normalizedPayload.offset ?? DEFAULT_OFFSET) <= DEFAULT_OFFSET) {
                    return incomingRows;
                }
                return [...previousRows, ...incomingRows];
            });
            return response.data;
        } catch (err: any) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') {
                return null;
            }
            const message = err?.response?.data?.message || err?.message || 'Failed to fetch Price Pack Architecture';
            setError(message);
            setData(null);
            setRows([]);
            return null;
        } finally {
            if (abortRef.current === controller) {
                setLoading(false);
                setIsPaginatedLoading(false);
            }
        }
    }, []);

    return {
        data,
        rows,
        loading,
        isPaginatedLoading,
        error,
        totalCount: data?.total_count ?? 0,
        fetchPricePackArchitecture,
    };
}

